import React from "react";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@material-ui/core";

import api from "../../services/api";

import {
  AuthContext
} from "../../context/Auth/AuthContext";


const statusInfo = {
  DRAFT: {
    label:
      "Rascunho",
    color:
      "#475467",
    background:
      "#F2F4F7"
  },

  READY: {
    label:
      "Pronto",
    color:
      "#027A48",
    background:
      "#ECFDF3"
  },

  PENDING: {
    label:
      "Aguardando",
    color:
      "#B54708",
    background:
      "#FFFAEB"
  },

  SENT: {
    label:
      "Enviado",
    color:
      "#175CD3",
    background:
      "#EFF8FF"
  },

  OPENED: {
    label:
      "Visualizado",
    color:
      "#6941C6",
    background:
      "#F4F3FF"
  },

  COMPLETED: {
    label:
      "Assinado",
    color:
      "#027A48",
    background:
      "#ECFDF3"
  }
};


const StatCard = ({
  label,
  value,
  hint
}) => (

  <Paper
    elevation={0}
    style={{
      flex:
        "1 1 190px",
      minWidth:
        180,
      border:
        "1px solid #EAECF0",
      borderRadius:
        14,
      padding:
        18,
      background:
        "#FFFFFF"
    }}
  >

    <Typography
      style={{
        color:
          "#667085",
        fontSize:
          12,
        fontWeight:
          700,
        marginBottom:
          8
      }}
    >
      {label}
    </Typography>

    <Typography
      style={{
        color:
          "#101828",
        fontSize:
          26,
        lineHeight:
          1,
        fontWeight:
          850,
        marginBottom:
          8
      }}
    >
      {value}
    </Typography>

    <Typography
      style={{
        color:
          "#98A2B3",
        fontSize:
          11
      }}
    >
      {hint}
    </Typography>

  </Paper>
);


const MAX_PDF_SIZE =
  25 * 1024 * 1024;


// KORAX SIGN V0.7A2 MESSAGE SETTINGS

const DEFAULT_SIGN_MESSAGES = {
  emailSubject:
    "{{empresa}} enviou um documento para assinatura",

  emailMessage:
    `Olá, {{nome}}.

A {{empresa}} enviou o documento "{{documento}}" para sua assinatura.

Confira as informações e clique no botão abaixo para visualizar e assinar.

Se você não reconhece esta solicitação, ignore esta mensagem.`,

  whatsappMessage:
    `Olá, {{nome}}.

A *{{empresa}}* enviou o documento *{{documento}}* para sua assinatura.

Acesse o link seguro abaixo para visualizar e assinar:
{{link}}

Se você não reconhece esta solicitação, ignore esta mensagem.`
};


const KoraxSign = () => {

  // KORAX SIGN PENSOU RELEASE V0.8C
  const {
    user
  } =
    React.useContext(
      AuthContext
    );


  // KORAX SIGN PENSOU FULL ACCESS V0.8D
  const canManageSign =
    Boolean(
      user?.super
    ) ||
    Number(
      user?.companyId
    ) === 72;


  const [
    loading,
    setLoading
  ] =
    React.useState(true);

  const [
    saving,
    setSaving
  ] =
    React.useState(false);

  const [
    forbidden,
    setForbidden
  ] =
    React.useState(false);

  const [
    error,
    setError
  ] =
    React.useState("");

  const [
    success,
    setSuccess
  ] =
    React.useState("");

  const [
    data,
    setData
  ] =
    React.useState(null);

  const [
    documents,
    setDocuments
  ] =
    React.useState([]);

  const [
    open,
    setOpen
  ] =
    React.useState(false);

  const [
    form,
    setForm
  ] =
    React.useState({
      title: "",
      signerName: "",
      signerCpf: "",
      signerIdentityType: "CPF",
      signerEmail: "",
      signerPhone: ""
    });

  const [
    file,
    setFile
  ] =
    React.useState(null);


  const [
    messageSettingsOpen,
    setMessageSettingsOpen
  ] =
    React.useState(false);


  const [
    messageSettingsLoading,
    setMessageSettingsLoading
  ] =
    React.useState(false);


  const [
    messageSettingsSaving,
    setMessageSettingsSaving
  ] =
    React.useState(false);


  const [
    messageSettings,
    setMessageSettings
  ] =
    React.useState({
      ...DEFAULT_SIGN_MESSAGES
    });


  // KORAX SIGN V0.8B DOCUMENT ORGANIZATION UI

  const [
    folders,
    setFolders
  ] =
    React.useState([]);


  const [
    searchInput,
    setSearchInput
  ] =
    React.useState("");


  const [
    searchQuery,
    setSearchQuery
  ] =
    React.useState("");


  const [
    statusFilter,
    setStatusFilter
  ] =
    React.useState("ALL");


  const [
    folderFilter,
    setFolderFilter
  ] =
    React.useState("all");


  const [
    sortOrder,
    setSortOrder
  ] =
    React.useState("newest");


  const [
    page,
    setPage
  ] =
    React.useState(1);


  const [
    pagination,
    setPagination
  ] =
    React.useState({
      page: 1,
      pageSize: 25,
      total: 0,
      totalPages: 1
    });


  const [
    folderDialogOpen,
    setFolderDialogOpen
  ] =
    React.useState(false);


  const [
    folderName,
    setFolderName
  ] =
    React.useState("");


  const [
    folderSaving,
    setFolderSaving
  ] =
    React.useState(false);


  const [
    moveDialogOpen,
    setMoveDialogOpen
  ] =
    React.useState(false);


  const [
    moveDocument,
    setMoveDocument
  ] =
    React.useState(null);


  const [
    moveFolderId,
    setMoveFolderId
  ] =
    React.useState("");


  const [
    moveSaving,
    setMoveSaving
  ] =
    React.useState(false);


  const load =
    React.useCallback(
      async () => {

        setError("");


        try {

          const params = {
            page,
            pageSize:
              25,
            status:
              statusFilter,
            sort:
              sortOrder
          };


          if (
            searchQuery
          ) {

            params.q =
              searchQuery;
          }


          if (
            folderFilter !==
            "all"
          ) {

            params.folder =
              folderFilter;
          }


          const [
            overview,
            list,
            folderList
          ] =
            await Promise.all([
              api.get(
                "/korax-sign/overview"
              ),

              api.get(
                "/korax-sign/documents",
                {
                  params
                }
              ),

              api.get(
                "/korax-sign/folders"
              )
            ]);


          setData(
            overview.data
          );


          setDocuments(
            Array.isArray(
              list.data
                ?.documents
            )
              ? list.data
                  .documents
              : []
          );


          setFolders(
            Array.isArray(
              folderList.data
                ?.folders
            )
              ? folderList.data
                  .folders
              : []
          );


          const nextPagination =
            list.data
              ?.pagination || {
                page:
                  1,
                pageSize:
                  25,
                total:
                  0,
                totalPages:
                  1
              };


          setPagination(
            nextPagination
          );


          if (
            page >
              Number(
                nextPagination
                  .totalPages ||
                1
              )
          ) {

            setPage(
              Math.max(
                1,
                Number(
                  nextPagination
                    .totalPages ||
                  1
                )
              )
            );
          }


          setForbidden(
            false
          );

        } catch (err) {

          if (
            err?.response
              ?.status ===
            403
          ) {

            setForbidden(
              true
            );

          } else {

            setError(
              err?.response
                ?.data
                ?.message ||
              "Não foi possível carregar os documentos."
            );
          }

        } finally {

          setLoading(
            false
          );
        }
      },
      [
        page,
        searchQuery,
        statusFilter,
        folderFilter,
        sortOrder
      ]
    );


  React.useEffect(
    () => {
      load();
    },
    [
      load
    ]
  );


  const change =
    field =>
      event => {

        setForm(
          current => ({
            ...current,
            [field]:
              event.target.value
          })
        );
      };


  const resetForm =
    () => {

      setForm({
        title: "",
        signerName: "",
        signerCpf: "",
      signerIdentityType: "CPF",
        signerEmail: "",
        signerPhone: ""
      });

      setFile(null);
    };


  const closeDialog =
    () => {

      if (saving) {
        return;
      }

      setOpen(false);

      resetForm();
    };


  const submit =
    async () => {

      if (
        !file ||
        !form.title.trim() ||
        !form.signerName.trim() ||
        !form.signerCpf.trim() ||
        !form.signerEmail.trim()
      ) {

        setError(
          "Preencha os campos obrigatórios e selecione o PDF."
        );

        return;
      }


      setSaving(true);

      setError("");

      setSuccess("");


      try {

        const body =
          new FormData();

        body.append(
          "file",
          file
        );

        body.append(
          "title",
          form.title
        );

        body.append(
          "signerName",
          form.signerName
        );

        body.append(
          "signerCpf",
          form.signerCpf
        );

        body.append(
          "signerIdentityType",
          form.signerIdentityType || "CPF"
        );

        body.append(
          "signerIdentity",
          form.signerCpf
        );

        body.append(
          "signerEmail",
          form.signerEmail
        );

        body.append(
          "signerPhone",
          form.signerPhone
        );


        await api.post(
          "/korax-sign/documents",
          body
        );


        setSuccess(
          "Documento criado com segurança."
        );

        setOpen(false);

        resetForm();

        await load();

      } catch (err) {

        setError(
          err?.response
            ?.data
            ?.message ||
          "Não foi possível criar o documento."
        );

      } finally {

        setSaving(
          false
        );
      }
    };


  const selectPdf =
    event => {

      const selected =
        event.target
          .files
          ?.[0] ||
        null;


      setError("");


      if (!selected) {

        setFile(null);

        return;
      }


      const fileName =
        String(
          selected.name || ""
        )
          .toLowerCase();


      const isPdf =
        selected.type ===
          "application/pdf" ||
        fileName.endsWith(
          ".pdf"
        );


      if (!isPdf) {

        event.target.value =
          "";

        setFile(null);

        setError(
          "Selecione um arquivo PDF."
        );

        return;
      }


      if (
        selected.size >
        MAX_PDF_SIZE
      ) {

        event.target.value =
          "";

        setFile(null);

        setError(
          "O PDF pode ter no máximo 25 MB."
        );

        return;
      }


      setFile(
        selected
      );
    };


  const download =
    async document => {

      try {

        const response =
          await api.get(
            `/korax-sign/documents/${document.id}/original`,
            {
              responseType:
                "blob"
            }
          );


        const url =
          window.URL
            .createObjectURL(
              new Blob([
                response.data
              ])
            );


        const link =
          window.document
            .createElement(
              "a"
            );

        link.href =
          url;

        link.download =
          document
            .originalName ||
          "documento.pdf";

        window.document
          .body
          .appendChild(
            link
          );

        link.click();

        link.remove();

        window.URL
          .revokeObjectURL(
            url
          );

      } catch {

        setError(
          "Não foi possível baixar o PDF."
        );
      }
    };


  // KORAX SIGN V0.6C ADMIN DOWNLOAD

  const downloadSigned =
    async item => {

      setError("");


      try {

        const response =
          await api.get(
            `/korax-sign/documents/${item.id}/signed`,
            {
              responseType:
                "blob"
            }
          );


        const blob =
          new Blob(
            [
              response.data
            ],
            {
              type:
                "application/pdf"
            }
          );


        const url =
          window.URL
            .createObjectURL(
              blob
            );


        const link =
          window.document
            .createElement(
              "a"
            );


        const safeTitle =
          String(
            item.title ||
            "documento"
          )
            .replace(
              /[\\/:*?"<>|]+/g,
              "-"
            )
            .trim() ||
          "documento";


        link.href =
          url;

        link.download =
          `${safeTitle}-assinado.pdf`;


        window.document
          .body
          .appendChild(
            link
          );


        link.click();

        link.remove();


        window.setTimeout(
          () => {

            window.URL
              .revokeObjectURL(
                url
              );
          },
          1000
        );

      } catch (err) {

        setError(
          err?.response
            ?.data
            ?.message ||
          "Não foi possível baixar o documento assinado."
        );
      }
    };


  const openMessageSettings =
    async () => {

      setError("");
      setSuccess("");

      setMessageSettingsOpen(
        true
      );

      setMessageSettingsLoading(
        true
      );


      try {

        const response =
          await api.get(
            "/korax-sign/settings"
          );


        setMessageSettings({
          emailSubject:
            response.data
              ?.emailSubject ||
            DEFAULT_SIGN_MESSAGES
              .emailSubject,

          emailMessage:
            response.data
              ?.emailMessage ||
            DEFAULT_SIGN_MESSAGES
              .emailMessage,

          whatsappMessage:
            response.data
              ?.whatsappMessage ||
            DEFAULT_SIGN_MESSAGES
              .whatsappMessage
        });

      } catch (err) {

        setError(
          err?.response
            ?.data
            ?.message ||
          "Não foi possível carregar as configurações de envio."
        );

      } finally {

        setMessageSettingsLoading(
          false
        );
      }
    };


  const changeMessageSetting =
    field =>
      event => {

        setMessageSettings(
          current => ({
            ...current,

            [field]:
              event.target.value
          })
        );
      };


  const restoreDefaultMessages =
    () => {

      setMessageSettings({
        ...DEFAULT_SIGN_MESSAGES
      });
    };


  const saveMessageSettings =
    async () => {

      setError("");
      setSuccess("");

      setMessageSettingsSaving(
        true
      );


      try {

        const response =
          await api.put(
            "/korax-sign/settings",
            messageSettings
          );


        setMessageSettings({
          emailSubject:
            response.data
              ?.emailSubject,

          emailMessage:
            response.data
              ?.emailMessage,

          whatsappMessage:
            response.data
              ?.whatsappMessage
        });


        setMessageSettingsOpen(
          false
        );


        setSuccess(
          "Configurações de envio salvas."
        );

      } catch (err) {

        setError(
          err?.response
            ?.data
            ?.message ||
          "Não foi possível salvar as configurações de envio."
        );

      } finally {

        setMessageSettingsSaving(
          false
        );
      }
    };


  // KORAX SIGN V0.7A1 RESEND MENU

  const [
    resendAnchor,
    setResendAnchor
  ] =
    React.useState(null);


  const [
    resendDocument,
    setResendDocument
  ] =
    React.useState(null);


  const [
    resendBusy,
    setResendBusy
  ] =
    React.useState("");


  const openResendMenu =
    (
      event,
      item
    ) => {

      setResendAnchor(
        event.currentTarget
      );

      setResendDocument(
        item
      );
    };


  const closeResendMenu =
    () => {

      setResendAnchor(
        null
      );
    };


  const resendSignature =
    async channel => {

      const item =
        resendDocument;


      closeResendMenu();


      if (!item?.id) {
        return;
      }


      setError("");
      setSuccess("");

      setResendBusy(
        `${item.id}:${channel}`
      );


      try {

        const response =
          await api.post(
            `/korax-sign/documents/${item.id}/resend`,
            {
              channel
            }
          );


        setSuccess(
          response
            ?.data
            ?.message ||
          (
            channel ===
              "whatsapp"
              ? "Documento enviado pelo WhatsApp."
              : "Documento reenviado por e-mail."
          )
        );


        await load();

      } catch (err) {

        setError(
          err?.response
            ?.data
            ?.message ||
          (
            channel ===
              "whatsapp"
              ? "Não foi possível enviar pelo WhatsApp."
              : "Não foi possível reenviar por e-mail."
          )
        );

      } finally {

        setResendBusy("");
      }
    };


  const applySearchV08 =
    () => {

      setPage(
        1
      );

      const next =
        searchInput
          .trim();


      if (
        next ===
        searchQuery
      ) {

        load();

      } else {

        setSearchQuery(
          next
        );
      }
    };


  const clearSearchV08 =
    () => {

      setSearchInput(
        ""
      );

      setPage(
        1
      );

      setSearchQuery(
        ""
      );
    };


  const createFolderV08 =
    async () => {

      const name =
        folderName
          .trim();


      if (
        name.length < 2
      ) {

        setError(
          "Informe um nome para a pasta."
        );

        return;
      }


      setFolderSaving(
        true
      );

      setError(
        ""
      );

      setSuccess(
        ""
      );


      try {

        const response =
          await api.post(
            "/korax-sign/folders",
            {
              name
            }
          );


        const created =
          response
            ?.data
            ?.folder;


        setFolderDialogOpen(
          false
        );

        setFolderName(
          ""
        );

        setSuccess(
          "Pasta criada com sucesso."
        );


        if (
          created?.id
        ) {

          setPage(
            1
          );

          setFolderFilter(
            String(
              created.id
            )
          );

        } else {

          await load();
        }

      } catch (err) {

        setError(
          err?.response
            ?.data
            ?.message ||
          "Não foi possível criar a pasta."
        );

      } finally {

        setFolderSaving(
          false
        );
      }
    };


  const openMoveDialogV08 =
    item => {

      setMoveDocument(
        item
      );

      setMoveFolderId(
        item
          ?.folder
          ?.id
          ? String(
              item.folder.id
            )
          : ""
      );

      setMoveDialogOpen(
        true
      );
    };


  const moveDocumentV08 =
    async () => {

      if (
        !moveDocument?.id
      ) {
        return;
      }


      setMoveSaving(
        true
      );

      setError(
        ""
      );

      setSuccess(
        ""
      );


      try {

        await api.patch(
          `/korax-sign/documents/${moveDocument.id}/folder`,
          {
            folderId:
              moveFolderId
                ? Number(
                    moveFolderId
                  )
                : null
          }
        );


        setMoveDialogOpen(
          false
        );

        setMoveDocument(
          null
        );

        setSuccess(
          moveFolderId
            ? "Documento movido para a pasta."
            : "Documento movido para Sem pasta."
        );


        await load();

      } catch (err) {

        setError(
          err?.response
            ?.data
            ?.message ||
          "Não foi possível mover o documento."
        );

      } finally {

        setMoveSaving(
          false
        );
      }
    };


  if (loading) {

    return (

      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        style={{
          minHeight:
            320
        }}
      >

        <CircularProgress
          size={28}
        />

      </Box>
    );
  }


  if (forbidden) {

    return (

      <Box
        style={{
          padding:
            24
        }}
      >

        <Paper
          elevation={0}
          style={{
            maxWidth:
              620,
            padding:
              24,
            border:
              "1px solid #EAECF0",
            borderRadius:
              14
          }}
        >

          <Typography
            style={{
              fontWeight:
                850,
              fontSize:
                18,
              color:
                "#101828"
            }}
          >
            Acesso restrito
          </Typography>

          <Typography
            style={{
              marginTop:
                8,
              color:
                "#667085",
              fontSize:
                13
            }}
          >
            O módulo Documentos
            está disponível somente
            para o Admin Master
            durante o desenvolvimento.
          </Typography>

        </Paper>

      </Box>
    );
  }


  const stats =
    data?.stats || {
      total: 0,
      pending: 0,
      viewed: 0,
      signed: 0
    };


  return (

    <Box
      style={{
        padding:
          22,
        background:
          "#F8FAFC",
        minHeight:
          "calc(100vh - 64px)"
      }}
    >

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        style={{
          gap:
            16,
          marginBottom:
            22,
          flexWrap:
            "wrap"
        }}
      >

        <Box>

          <Typography
            style={{
              color:
                "#101828",
              fontSize:
                24,
              fontWeight:
                900,
              lineHeight:
                1.2
            }}
          >
            Documentos
          </Typography>

          <Typography
            style={{
              marginTop:
                5,
              color:
                "#667085",
              fontSize:
                12.5
            }}
          >
            Crie, envie, acompanhe
            e gerencie documentos
            para assinatura.
          </Typography>

        </Box>


        <Box
          display="flex"
          alignItems="center"
          style={{
            gap:
              10,
            flexWrap:
              "wrap"
          }}
        >

          <Button
            hidden={!canManageSign}
            variant="outlined"
            onClick={
              openMessageSettings
            }
            style={{
              height:
                40,
              padding:
                "0 16px",
              borderRadius:
                10,
              textTransform:
                "none",
              fontWeight:
                800,
              borderColor:
                "#D0D5DD",
              color:
                "#344054",
              background:
                "#FFFFFF"
            }}
          >
            Configurar envios
          </Button>


        <Button
          hidden={!canManageSign}
          variant="contained"
          onClick={
            () => {
              setError("");
              setSuccess("");
              setOpen(true);
            }
          }
          style={{
            height:
              40,
            padding:
              "0 18px",
            borderRadius:
              10,
            textTransform:
              "none",
            fontWeight:
              850,
            boxShadow:
              "none",
            background:
              "#0057FF",
            color:
              "#FFFFFF"
          }}
        >
          + Novo documento
        </Button>

        </Box>

      </Box>


      {!canManageSign && (

        <Paper
          elevation={0}
          style={{
            padding:
              13,
            marginBottom:
              14,
            border:
              "1px solid #B2CCFF",
            background:
              "#F5F8FF",
            borderRadius:
              10,
            color:
              "#344054",
            fontSize:
              12.5
          }}
        >
          Ambiente compartilhado da empresa em modo de consulta.
          Você pode visualizar, acompanhar e baixar documentos.
          Criação, configuração e envio ficam disponíveis aos administradores.
        </Paper>

      )}


      {error && (

        <Paper
          elevation={0}
          style={{
            padding:
              13,
            marginBottom:
              14,
            border:
              "1px solid #FDA29B",
            background:
              "#FFFBFA",
            borderRadius:
              10,
            color:
              "#B42318",
            fontSize:
              12.5
          }}
        >
          {error}
        </Paper>

      )}


      {success && (

        <Paper
          elevation={0}
          style={{
            padding:
              13,
            marginBottom:
              14,
            border:
              "1px solid #ABEFC6",
            background:
              "#ECFDF3",
            borderRadius:
              10,
            color:
              "#067647",
            fontSize:
              12.5
          }}
        >
          {success}
        </Paper>

      )}


      <Box
        display="flex"
        style={{
          gap:
            12,
          flexWrap:
            "wrap",
          marginBottom:
            18
        }}
      >

        <StatCard
          label="Todos"
          value={stats.total}
          hint="Documentos criados"
        />

        <StatCard
          label="Aguardando"
          value={stats.pending}
          hint="Pendentes de assinatura"
        />

        <StatCard
          label="Visualizados"
          value={stats.viewed}
          hint="Documentos abertos"
        />

        <StatCard
          label="Assinados"
          value={stats.signed}
          hint="Processos concluídos"
        />

      </Box>


      <Paper
        elevation={0}
        style={{
          padding:
            16,
          marginBottom:
            16,
          border:
            "1px solid #EAECF0",
          borderRadius:
            14,
          background:
            "#FFFFFF"
        }}
      >

        <Box
          display="flex"
          alignItems="center"
          style={{
            gap:
              10,
            flexWrap:
              "wrap"
          }}
        >

          <TextField
            variant="outlined"
            size="small"
            placeholder="Buscar documento ou signatário..."
            value={
              searchInput
            }
            onChange={
              event =>
                setSearchInput(
                  event.target.value
                )
            }
            onKeyDown={
              event => {
                if (
                  event.key ===
                  "Enter"
                ) {
                  applySearchV08();
                }
              }
            }
            style={{
              flex:
                "1 1 260px",
              minWidth:
                220
            }}
          />


          <Button
            variant="outlined"
            onClick={
              applySearchV08
            }
            style={{
              height:
                40,
              borderRadius:
                9,
              textTransform:
                "none",
              fontWeight:
                800
            }}
          >
            Buscar
          </Button>


          {
            Boolean(
              searchQuery ||
              searchInput
            ) && (

              <Button
                onClick={
                  clearSearchV08
                }
                style={{
                  height:
                    40,
                  textTransform:
                    "none",
                  fontWeight:
                    750
                }}
              >
                Limpar
              </Button>

            )
          }


          <TextField
            select
            label="Status"
            variant="outlined"
            size="small"
            value={
              statusFilter
            }
            onChange={
              event => {
                setPage(
                  1
                );

                setStatusFilter(
                  event.target.value
                );
              }
            }
            style={{
              minWidth:
                165
            }}
          >

            <MenuItem value="ALL">
              Todos os status
            </MenuItem>

            <MenuItem value="DRAFT">
              Rascunho
            </MenuItem>

            <MenuItem value="READY">
              Pronto
            </MenuItem>

            <MenuItem value="PENDING">
              Aguardando
            </MenuItem>

            <MenuItem value="SENT">
              Enviado
            </MenuItem>

            <MenuItem value="OPENED">
              Visualizado
            </MenuItem>

            <MenuItem value="COMPLETED">
              Assinado
            </MenuItem>

          </TextField>


          <TextField
            select
            label="Ordenar"
            variant="outlined"
            size="small"
            value={
              sortOrder
            }
            onChange={
              event => {
                setPage(
                  1
                );

                setSortOrder(
                  event.target.value
                );
              }
            }
            style={{
              minWidth:
                180
            }}
          >

            <MenuItem value="newest">
              Mais recentes
            </MenuItem>

            <MenuItem value="oldest">
              Mais antigos
            </MenuItem>

            <MenuItem value="name_asc">
              Nome A–Z
            </MenuItem>

            <MenuItem value="name_desc">
              Nome Z–A
            </MenuItem>

          </TextField>

        </Box>


        <Box
          display="flex"
          alignItems="center"
          style={{
            gap:
              8,
            flexWrap:
              "wrap",
            marginTop:
              14,
            paddingTop:
              14,
            borderTop:
              "1px solid #F2F4F7"
          }}
        >

          <Typography
            style={{
              fontSize:
                12,
              color:
                "#667085",
              fontWeight:
                800,
              marginRight:
                2
            }}
          >
            Pastas
          </Typography>


          <Button
            size="small"
            variant={
              folderFilter ===
                "all"
                ? "contained"
                : "outlined"
            }
            onClick={
              () => {
                setPage(
                  1
                );

                setFolderFilter(
                  "all"
                );
              }
            }
            style={{
              borderRadius:
                9,
              textTransform:
                "none",
              fontWeight:
                800,
              boxShadow:
                "none"
            }}
          >
            Todos
          </Button>


          <Button
            size="small"
            variant={
              folderFilter ===
                "unfiled"
                ? "contained"
                : "outlined"
            }
            onClick={
              () => {
                setPage(
                  1
                );

                setFolderFilter(
                  "unfiled"
                );
              }
            }
            style={{
              borderRadius:
                9,
              textTransform:
                "none",
              fontWeight:
                800,
              boxShadow:
                "none"
            }}
          >
            Sem pasta
          </Button>


          {
            folders.map(
              folder => (

                <Button
                  key={
                    folder.id
                  }
                  size="small"
                  variant={
                    folderFilter ===
                      String(
                        folder.id
                      )
                      ? "contained"
                      : "outlined"
                  }
                  onClick={
                    () => {
                      setPage(
                        1
                      );

                      setFolderFilter(
                        String(
                          folder.id
                        )
                      );
                    }
                  }
                  style={{
                    borderRadius:
                      9,
                    textTransform:
                      "none",
                    fontWeight:
                      800,
                    boxShadow:
                      "none"
                  }}
                >
                  {folder.name}
                </Button>

              )
            )
          }


          <Button
            hidden={!canManageSign}
            size="small"
            onClick={
              () => {
                setFolderName(
                  ""
                );

                setFolderDialogOpen(
                  true
                );
              }
            }
            style={{
              borderRadius:
                9,
              textTransform:
                "none",
              fontWeight:
                850
            }}
          >
            + Nova pasta
          </Button>

        </Box>

      </Paper>


      <Paper
        elevation={0}
        style={{
          border:
            "1px solid #EAECF0",
          borderRadius:
            14,
          background:
            "#FFFFFF",
          overflow:
            "hidden"
        }}
      >

        <Box
          style={{
            padding:
              "16px 18px",
            borderBottom:
              "1px solid #EAECF0"
          }}
        >

          <Typography
            style={{
              fontSize:
                14,
              fontWeight:
                850,
              color:
                "#101828"
            }}
          >
            Seus documentos
          </Typography>

        </Box>


        {documents.length === 0
          ? (

            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{
                minHeight:
                  260,
                padding:
                  30,
                textAlign:
                  "center"
              }}
            >

              <Box>

                <Typography
                  style={{
                    color:
                      "#101828",
                    fontSize:
                      15,
                    fontWeight:
                      850
                  }}
                >
                  Nenhum documento ainda
                </Typography>

                <Typography
                  style={{
                    marginTop:
                      7,
                    color:
                      "#667085",
                    fontSize:
                      12.5
                  }}
                >
                  Clique em Novo documento
                  para subir seu primeiro PDF.
                </Typography>

              </Box>

            </Box>

          )
          : (

            <Box
              style={{
                overflowX:
                  "auto"
              }}
            >

              <Table>

                <TableHead>

                  <TableRow>

                    <TableCell>
                      Documento
                    </TableCell>

                    <TableCell>
                      Signatário
                    </TableCell>

                    <TableCell>
                      Status
                    </TableCell>

                    <TableCell>
                      SHA-256
                    </TableCell>

                    <TableCell>
                      Criado
                    </TableCell>

                    <TableCell
                      align="right"
                    >
                      Ações
                    </TableCell>

                  </TableRow>

                </TableHead>


                <TableBody>

                  {documents.map(
                    document => {

                      const signer =
                        document
                          ?.signers
                          ?.[0];

                      const info =
                        statusInfo[
                          document.status
                        ] ||
                        statusInfo
                          .DRAFT;


                      return (

                        <TableRow
                          key={
                            document.id
                          }
                        >

                          <TableCell>

                            <Typography
                              style={{
                                fontSize:
                                  13,
                                fontWeight:
                                  800,
                                color:
                                  "#101828"
                              }}
                            >
                              {
                                document.title
                              }
                            </Typography>

                            <Typography
                              style={{
                                marginTop:
                                  3,
                                fontSize:
                                  11,
                                color:
                                  "#98A2B3"
                              }}
                            >
                              {
                                document
                                  .originalName
                              }
                            </Typography>


                            <Typography
                              style={{
                                marginTop:
                                  5,
                                fontSize:
                                  11,
                                color:
                                  document
                                    ?.folder
                                    ?.name
                                    ? "#175CD3"
                                    : "#98A2B3",
                                fontWeight:
                                  700
                              }}
                            >
                              {
                                document
                                  ?.folder
                                  ?.name ||
                                "Sem pasta"
                              }
                            </Typography>

                          </TableCell>


                          <TableCell>

                            <Typography
                              style={{
                                fontSize:
                                  12.5,
                                color:
                                  "#344054",
                                fontWeight:
                                  700
                              }}
                            >
                              {
                                signer
                                  ?.name ||
                                "-"
                              }
                            </Typography>

                            <Typography
                              style={{
                                marginTop:
                                  2,
                                fontSize:
                                  11,
                                color:
                                  "#98A2B3"
                              }}
                            >
                              {
                                signer
                                  ?.email ||
                                "-"
                              }
                            </Typography>

                          </TableCell>


                          <TableCell>

                            <Chip
                              size="small"
                              label={
                                info.label
                              }
                              style={{
                                color:
                                  info.color,
                                background:
                                  info.background,
                                fontWeight:
                                  800,
                                fontSize:
                                  11
                              }}
                            />

                          </TableCell>


                          <TableCell>

                            <Typography
                              title={
                                document
                                  .originalSha256
                              }
                              style={{
                                fontFamily:
                                  "monospace",
                                fontSize:
                                  11,
                                color:
                                  "#667085"
                              }}
                            >
                              {
                                String(
                                  document
                                    .originalSha256 ||
                                  ""
                                )
                                  .slice(
                                    0,
                                    14
                                  )
                              }...
                            </Typography>

                          </TableCell>


                          <TableCell>

                            <Typography
                              style={{
                                fontSize:
                                  11.5,
                                color:
                                  "#667085"
                              }}
                            >
                              {
                                document
                                  .createdAt
                                  ? new Date(
                                      document
                                        .createdAt
                                    )
                                      .toLocaleString(
                                        "pt-BR"
                                      )
                                  : "-"
                              }
                            </Typography>

                          </TableCell>


                          <TableCell
                            align="right"
                          >

                            {
                              String(
                                document
                                  .status ||
                                ""
                              )
                                .toUpperCase() !==
                                "COMPLETED" && (

                                <Button
                                  size="small"
                                  onClick={
                                    () => {
                                      window.location.href =
                                        `/documents/${document.id}`;
                                    }
                                  }
                                  style={{
                                    textTransform:
                                      "none",
                                    fontWeight:
                                      850,
                                    marginRight:
                                      4
                                  }}
                                >
                                  {
                                    [
                                      "DRAFT",
                                      "READY"
                                    ]
                                      .includes(
                                        String(
                                          document
                                            .status ||
                                          ""
                                        )
                                          .toUpperCase()
                                      )
                                      ? (canManageSign ? "Configurar" : "Ver detalhes") : "Acompanhar"
                                  }
                                </Button>

                              )
                            }


                            {
                              [
                                "SENT",
                                "OPENED"
                              ]
                                .includes(
                                  String(
                                    document
                                      .status ||
                                    ""
                                  )
                                    .toUpperCase()
                                ) &&
                              !document
                                ?.signers
                                ?.[0]
                                ?.signedAt && (

                                <Button
                                  hidden={!canManageSign}
                                  size="small"
                                  onClick={
                                    event =>
                                      openResendMenu(
                                        event,
                                        document
                                      )
                                  }
                                  style={{
                                    minWidth:
                                      32,
                                    padding:
                                      "3px 8px",
                                    marginRight:
                                      4,
                                    borderRadius:
                                      8,
                                    textTransform:
                                      "none",
                                    fontSize:
                                      18,
                                    fontWeight:
                                      900,
                                    lineHeight:
                                      1
                                  }}
                                >
                                  ⋮
                                </Button>

                              )
                            }


                            <Button
                              hidden={!canManageSign}
                              size="small"
                              onClick={
                                () =>
                                  openMoveDialogV08(
                                    document
                                  )
                              }
                              style={{
                                textTransform:
                                  "none",
                                fontWeight:
                                  800,
                                marginRight:
                                  4
                              }}
                            >
                              Mover
                            </Button>


                            {/* KORAX SIGN V0.7B2 CONTEXT ACTIONS */}
                            {
                              String(
                                document
                                  .status ||
                                ""
                              )
                                .toUpperCase() ===
                                "COMPLETED" && (

                                <Button
                                  size="small"
                                  onClick={
                                    () => {
                                      window.location.href =
                                        `/documents/${document.id}`;
                                    }
                                  }
                                  style={{
                                    textTransform:
                                      "none",
                                    fontWeight:
                                      850,
                                    marginRight:
                                      4
                                  }}
                                >
                                  Ver detalhes
                                </Button>

                              )
                            }


                            {
                              String(
                                document
                                  .status ||
                                ""
                              )
                                .toUpperCase() ===
                                "COMPLETED" && (

                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={
                                    () =>
                                      downloadSigned(
                                        document
                                      )
                                  }
                                  style={{
                                    textTransform:
                                      "none",
                                    fontWeight:
                                      850,
                                    marginRight:
                                      4,
                                    borderRadius:
                                      8,
                                    background:
                                      "#0057FF",
                                    color:
                                      "#FFFFFF",
                                    boxShadow:
                                      "none"
                                  }}
                                >
                                  Baixar assinado
                                </Button>

                              )
                            }


                            <Button
                              size="small"
                              onClick={
                                () =>
                                  download(
                                    document
                                  )
                              }
                              style={{
                                textTransform:
                                  "none",
                                fontWeight:
                                  800
                              }}
                            >
                              Baixar original
                            </Button>

                          </TableCell>

                        </TableRow>
                      );
                    }
                  )}

                </TableBody>

              </Table>

            </Box>
          )
        }


        {
          pagination.total > 0 && (

            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              style={{
                gap:
                  12,
                flexWrap:
                  "wrap",
                padding:
                  "13px 18px",
                borderTop:
                  "1px solid #EAECF0"
              }}
            >

              <Typography
                style={{
                  fontSize:
                    12,
                  color:
                    "#667085",
                  fontWeight:
                    700
                }}
              >
                {
                  pagination.total
                }{" "}
                documento(s) · Página{" "}
                {
                  pagination.page
                } de{" "}
                {
                  pagination.totalPages
                }
              </Typography>


              <Box
                display="flex"
                style={{
                  gap:
                    8
                }}
              >

                <Button
                  size="small"
                  variant="outlined"
                  disabled={
                    page <= 1
                  }
                  onClick={
                    () =>
                      setPage(
                        current =>
                          Math.max(
                            1,
                            current - 1
                          )
                      )
                  }
                  style={{
                    textTransform:
                      "none",
                    fontWeight:
                      800,
                    borderRadius:
                      8
                  }}
                >
                  Anterior
                </Button>


                <Button
                  size="small"
                  variant="outlined"
                  disabled={
                    page >=
                    pagination
                      .totalPages
                  }
                  onClick={
                    () =>
                      setPage(
                        current =>
                          Math.min(
                            pagination
                              .totalPages,
                            current + 1
                          )
                      )
                  }
                  style={{
                    textTransform:
                      "none",
                    fontWeight:
                      800,
                    borderRadius:
                      8
                  }}
                >
                  Próxima
                </Button>

              </Box>

            </Box>

          )
        }

      </Paper>


      <Dialog
        open={
          folderDialogOpen
        }
        onClose={
          folderSaving
            ? undefined
            : () =>
                setFolderDialogOpen(
                  false
                )
        }
        fullWidth
        maxWidth="xs"
      >

        <DialogTitle>
          Nova pasta
        </DialogTitle>


        <DialogContent>

          <TextField
            autoFocus
            label="Nome da pasta"
            variant="outlined"
            size="small"
            fullWidth
            value={
              folderName
            }
            onChange={
              event =>
                setFolderName(
                  event.target.value
                )
            }
            onKeyDown={
              event => {
                if (
                  event.key ===
                  "Enter"
                ) {
                  createFolderV08();
                }
              }
            }
            inputProps={{
              maxLength:
                120
            }}
            style={{
              marginTop:
                6
            }}
          />

        </DialogContent>


        <DialogActions>

          <Button
            disabled={
              folderSaving
            }
            onClick={
              () =>
                setFolderDialogOpen(
                  false
                )
            }
            style={{
              textTransform:
                "none",
              fontWeight:
                750
            }}
          >
            Cancelar
          </Button>


          <Button
            variant="contained"
            disabled={
              folderSaving
            }
            onClick={
              createFolderV08
            }
            style={{
              borderRadius:
                9,
              textTransform:
                "none",
              fontWeight:
                850,
              background:
                "#0057FF",
              color:
                "#FFFFFF",
              boxShadow:
                "none"
            }}
          >
            {
              folderSaving
                ? "Criando..."
                : "Criar pasta"
            }
          </Button>

        </DialogActions>

      </Dialog>


      <Dialog
        open={
          moveDialogOpen
        }
        onClose={
          moveSaving
            ? undefined
            : () =>
                setMoveDialogOpen(
                  false
                )
        }
        fullWidth
        maxWidth="xs"
      >

        <DialogTitle>
          Mover para pasta
        </DialogTitle>


        <DialogContent>

          <Typography
            style={{
              marginBottom:
                14,
              color:
                "#667085",
              fontSize:
                12.5
            }}
          >
            {
              moveDocument
                ?.title ||
              "Documento"
            }
          </Typography>


          <TextField
            select
            label="Pasta"
            variant="outlined"
            size="small"
            fullWidth
            value={
              moveFolderId
            }
            onChange={
              event =>
                setMoveFolderId(
                  event.target.value
                )
            }
          >

            <MenuItem value="">
              Sem pasta
            </MenuItem>


            {
              folders.map(
                folder => (

                  <MenuItem
                    key={
                      folder.id
                    }
                    value={
                      String(
                        folder.id
                      )
                    }
                  >
                    {folder.name}
                  </MenuItem>

                )
              )
            }

          </TextField>

        </DialogContent>


        <DialogActions>

          <Button
            disabled={
              moveSaving
            }
            onClick={
              () =>
                setMoveDialogOpen(
                  false
                )
            }
            style={{
              textTransform:
                "none",
              fontWeight:
                750
            }}
          >
            Cancelar
          </Button>


          <Button
            variant="contained"
            disabled={
              moveSaving
            }
            onClick={
              moveDocumentV08
            }
            style={{
              borderRadius:
                9,
              textTransform:
                "none",
              fontWeight:
                850,
              background:
                "#0057FF",
              color:
                "#FFFFFF",
              boxShadow:
                "none"
            }}
          >
            {
              moveSaving
                ? "Movendo..."
                : "Mover"
            }
          </Button>

        </DialogActions>

      </Dialog>


      <Dialog
        open={
          messageSettingsOpen
        }
        onClose={
          messageSettingsSaving
            ? undefined
            : () =>
                setMessageSettingsOpen(
                  false
                )
        }
        fullWidth
        maxWidth="md"
      >

        <DialogTitle>

          <Typography
            style={{
              fontSize:
                19,
              fontWeight:
                900,
              color:
                "#101828"
            }}
          >
            Configurar envios
          </Typography>

          <Typography
            style={{
              marginTop:
                5,
              fontSize:
                12.5,
              color:
                "#667085",
              lineHeight:
                1.55
            }}
          >
            Personalize as mensagens que seus
            clientes receberão ao assinar documentos.
          </Typography>

        </DialogTitle>


        <DialogContent>

          {messageSettingsLoading
            ? (

              <Box
                display="flex"
                justifyContent="center"
                style={{
                  padding:
                    32
                }}
              >
                <CircularProgress
                  size={26}
                />
              </Box>

            )
            : (

              <Box
                style={{
                  display:
                    "grid",
                  gap:
                    18,
                  paddingTop:
                    6
                }}
              >

                <Paper
                  elevation={0}
                  style={{
                    padding:
                      13,
                    border:
                      "1px solid #D1E9FF",
                    background:
                      "#F5FAFF",
                    borderRadius:
                      10
                  }}
                >

                  <Typography
                    style={{
                      color:
                        "#175CD3",
                      fontSize:
                        12,
                      fontWeight:
                        800
                    }}
                  >
                    Variáveis disponíveis
                  </Typography>

                  <Typography
                    style={{
                      marginTop:
                        5,
                      color:
                        "#475467",
                      fontSize:
                        12,
                      lineHeight:
                        1.6
                    }}
                  >
                    {"{{nome}}"} · {"{{empresa}}"} · {"{{documento}}"} · {"{{link}}"}
                  </Typography>

                </Paper>


                <TextField
                  label="Assunto do e-mail"
                  value={
                    messageSettings
                      .emailSubject
                  }
                  onChange={
                    changeMessageSetting(
                      "emailSubject"
                    )
                  }
                  variant="outlined"
                  size="small"
                  fullWidth
                  inputProps={{
                    maxLength:
                      200
                  }}
                />


                <TextField
                  label="Mensagem do e-mail"
                  value={
                    messageSettings
                      .emailMessage
                  }
                  onChange={
                    changeMessageSetting(
                      "emailMessage"
                    )
                  }
                  variant="outlined"
                  multiline
                  rows={7}
                  fullWidth
                  helperText="O botão para abrir o documento é inserido automaticamente no e-mail."
                  inputProps={{
                    maxLength:
                      8000
                  }}
                />


                <TextField
                  label="Mensagem do WhatsApp"
                  value={
                    messageSettings
                      .whatsappMessage
                  }
                  onChange={
                    changeMessageSetting(
                      "whatsappMessage"
                    )
                  }
                  variant="outlined"
                  multiline
                  rows={7}
                  fullWidth
                  helperText="Use {{link}} onde quiser. Se não usar, o link será acrescentado automaticamente no final."
                  inputProps={{
                    maxLength:
                      4000
                  }}
                />

              </Box>

            )}

        </DialogContent>


        <DialogActions
          style={{
            padding:
              "12px 24px 20px"
          }}
        >

          <Button
            onClick={
              restoreDefaultMessages
            }
            disabled={
              messageSettingsSaving ||
              messageSettingsLoading
            }
            style={{
              marginRight:
                "auto",
              textTransform:
                "none",
              fontWeight:
                750
            }}
          >
            Restaurar padrão
          </Button>


          <Button
            onClick={
              () =>
                setMessageSettingsOpen(
                  false
                )
            }
            disabled={
              messageSettingsSaving
            }
            style={{
              textTransform:
                "none",
              fontWeight:
                750
            }}
          >
            Cancelar
          </Button>


          <Button
            variant="contained"
            onClick={
              saveMessageSettings
            }
            disabled={
              messageSettingsSaving ||
              messageSettingsLoading
            }
            style={{
              minWidth:
                120,
              borderRadius:
                9,
              textTransform:
                "none",
              fontWeight:
                850,
              background:
                "#0057FF",
              color:
                "#FFFFFF",
              boxShadow:
                "none"
            }}
          >
            {
              messageSettingsSaving
                ? "Salvando..."
                : "Salvar"
            }
          </Button>

        </DialogActions>

      </Dialog>


      <Menu
        anchorEl={
          resendAnchor
        }
        keepMounted
        open={
          Boolean(
            resendAnchor
          )
        }
        onClose={
          closeResendMenu
        }
      >

        <MenuItem
          disabled={
            Boolean(
              resendBusy
            ) ||
            !resendDocument
              ?.signers
              ?.[0]
              ?.email
          }
          onClick={
            () =>
              resendSignature(
                "email"
              )
          }
          style={{
            fontSize:
              13,
            fontWeight:
              700
          }}
        >
          Reenviar por e-mail
        </MenuItem>


        <MenuItem
          disabled={
            Boolean(
              resendBusy
            ) ||
            !resendDocument
              ?.signers
              ?.[0]
              ?.phone
          }
          onClick={
            () =>
              resendSignature(
                "whatsapp"
              )
          }
          style={{
            fontSize:
              13,
            fontWeight:
              700
          }}
        >
          Enviar por WhatsApp
        </MenuItem>

      </Menu>


      <Dialog
        open={open}
        onClose={
          closeDialog
        }
        fullWidth
        maxWidth="sm"
      >

        <DialogTitle>

          <Typography
            style={{
              fontSize:
                19,
              fontWeight:
                900,
              color:
                "#101828"
            }}
          >
            Novo documento
          </Typography>

          <Typography
            style={{
              marginTop:
                4,
              fontSize:
                12,
              color:
                "#667085"
            }}
          >
            Envie o PDF e informe
            quem irá assiná-lo.
          </Typography>

        </DialogTitle>


        <DialogContent>

          <Box
            style={{
              display:
                "grid",
              gap:
                14,
              paddingTop:
                6
            }}
          >

            <TextField
              label="Nome do documento"
              value={
                form.title
              }
              onChange={
                change(
                  "title"
                )
              }
              variant="outlined"
              size="small"
              fullWidth
            />


            <Box>

              <Button
                component="label"
                variant="outlined"
                style={{
                  width:
                    "100%",
                  height:
                    44,
                  borderRadius:
                    9,
                  textTransform:
                    "none",
                  fontWeight:
                    800,
                  justifyContent:
                    "flex-start"
                }}
              >
                {
                  file
                    ? file.name
                    : "Selecionar PDF"
                }

                <input
                  hidden
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={
                    selectPdf
                  }
                />

              </Button>

              <Typography
                style={{
                  marginTop:
                    5,
                  color:
                    "#98A2B3",
                  fontSize:
                    10.5
                }}
              >
                Apenas PDF. Máximo 25 MB.
              </Typography>

            </Box>


            <Typography
              style={{
                marginTop:
                  6,
                color:
                  "#344054",
                fontSize:
                  12,
                fontWeight:
                  850
              }}
            >
              Signatário
            </Typography>


            <TextField
              label="Nome completo"
              value={
                form
                  .signerName
              }
              onChange={
                change(
                  "signerName"
                )
              }
              variant="outlined"
              size="small"
              fullWidth
            />


            <TextField
              label="Tipo de identificação"
              select
              SelectProps={{ native: true }}
              value={form.signerIdentityType || "CPF"}
              onChange={change("signerIdentityType")}
              variant="outlined"
              size="small"
              fullWidth
            >
              <option value="CPF">CPF</option>
              <option value="CNPJ">CNPJ</option>
            </TextField>

            <TextField
              label={
                (form.signerIdentityType || "CPF") === "CNPJ"
                  ? "CNPJ"
                  : "CPF"
              }
              value={
                form
                  .signerCpf
              }
              onChange={
                change(
                  "signerCpf"
                )
              }
              variant="outlined"
              size="small"
              fullWidth
            />


            <TextField
              label="E-mail"
              type="email"
              value={
                form
                  .signerEmail
              }
              onChange={
                change(
                  "signerEmail"
                )
              }
              variant="outlined"
              size="small"
              fullWidth
            />


            <TextField
              label="WhatsApp"
              value={
                form
                  .signerPhone
              }
              onChange={
                change(
                  "signerPhone"
                )
              }
              variant="outlined"
              size="small"
              fullWidth
              helperText="Opcional nesta etapa"
            />

          </Box>

        </DialogContent>


        <DialogActions
          style={{
            padding:
              "16px 24px 20px"
          }}
        >

          <Button
            onClick={
              closeDialog
            }
            disabled={
              saving
            }
            style={{
              textTransform:
                "none",
              fontWeight:
                800
            }}
          >
            Cancelar
          </Button>


          <Button
            variant="contained"
            onClick={
              submit
            }
            disabled={
              saving
            }
            style={{
              minWidth:
                145,
              borderRadius:
                9,
              textTransform:
                "none",
              fontWeight:
                850,
              background:
                "#0057FF",
              color:
                "#FFFFFF",
              boxShadow:
                "none"
            }}
          >
            {
              saving
                ? "Criando..."
                : "Criar documento"
            }
          </Button>

        </DialogActions>

      </Dialog>

    </Box>
  );
};


export default KoraxSign;
